/**
 * Constant-time string comparison utility to prevent Observable Timing
 * Discrepancy / timing side-channel attacks.
 *
 * Security: direct string equality (===) short-circuits on the first
 * mismatched character, leaking information about the expected value through
 * measurable response-time differences. Constant-time comparison always
 * iterates over every character regardless of where a mismatch occurs.
 *
 * This implementation is browser-compatible (no Node.js crypto required).
 */

/**
 * Compares two strings in constant time to prevent timing attacks.
 * Returns true only when both strings are identical in content and length.
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns boolean — true if strings are equal
 */
export function constantTimeEqual(a: string, b: string): boolean {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }

  const aBytes = new TextEncoder().encode(a);
  const bBytes = new TextEncoder().encode(b);

  // Always iterate the full length of the longer array to prevent early exit
  const maxLen = Math.max(aBytes.length, bBytes.length);
  let result = aBytes.length === bBytes.length ? 0 : 1;

  for (let i = 0; i < maxLen; i++) {
    // XOR: 0 when bytes match, non-zero when they differ
    // Bitwise OR accumulates any difference without branching
    result |= (aBytes[i] ?? 0) ^ (bBytes[i] ?? 0);
  }

  return result === 0;
}
